import axios from 'axios'
import { Message } from 'element-ui'
import { handleUnauthorized } from '@/utils/authSession'

const http = axios.create({
  baseURL: process.env.VUE_APP_API_BASE || 'http://localhost:5000/api',
  timeout: 15000
})

// 请求拦截：自动带上Token
http.interceptors.request.use(config => {
  const token = localStorage.getItem('wd_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }
  return config
})

// 响应拦截：统一错误处理
http.interceptors.response.use(
  res => res.data,
  err => {
    const status = err.response?.status
    if (status === 401) {
      handleUnauthorized(err)
    } else if (status === 403) {
      Message.error('权限不足')
    } else {
      Message.error(err.response?.data?.message || '请求失败')
    }
    return Promise.reject(err)
  }
)

// ─── Auth ─────────────────────────────────────────────────────────────────
export const authApi = {
  login: data => http.post('/auth/login', data),
  register: data => http.post('/auth/register', data),
  checkUsername: username => http.get('/auth/check-username', { params: { username } }),
  checkEmail: (email, phone) => http.get('/auth/check-email', { params: { email, phone } }),
  checkPhone: (phone, email) => http.get('/auth/check-phone', { params: { phone, email } }),
  me: () => http.get('/auth/me'),
  refresh: () => http.post('/auth/refresh'),
  changePassword: data => http.post('/auth/change-password', data)
}

// ─── Users ────────────────────────────────────────────────────────────────
export const userApi = {
  list: params => http.get('/user', { params }),
  update: (id, data) => http.put(`/user/${id}`, data),
  delete: id => http.delete(`/user/${id}`),
  resetPassword: (id, pwd) => http.post(`/user/${id}/reset-password`, pwd, {
    headers: { 'Content-Type': 'application/json' }
  })
}

// ─── Modules ──────────────────────────────────────────────────────────────
export const moduleApi = {
  list: () => http.get('/module'),
  tree: (params) => http.get('/module/tree', { params }),
  get: id => http.get(`/module/${id}`),
  create: data => http.post('/module', data),
  update: (id, data) => http.put(`/module/${id}`, data),
  delete: id => http.delete(`/module/${id}`)
}

// ─── Contents ─────────────────────────────────────────────────────────────
export const contentApi = {
  list: params => http.get('/content', { params }),
  get: id => http.get(`/content/${id}`),
  create: data => http.post('/content', data),
  update: (id, data) => http.put(`/content/${id}`, data),
  delete: id => http.delete(`/content/${id}`)
}

// ─── Industry ─────────────────────────────────────────────────────────────
export const industryApi = {
  nav: () => http.get('/excel/industry-nav'),
  rows: (moduleKey, params) => http.get(`/excel/rows/${moduleKey}`, { params })
}

// ─── Commodity ────────────────────────────────────────────────────────────
export const commodityApi = {
  // params 可选：{ timeRange, dateFrom, dateTo, priceType, categoryKey, page, pageSize }
  latest: (moduleKey, params) => http.get(`/excel/commodity/${moduleKey}`, { params }),
  series: (moduleKey, params) => http.get(`/excel/commodity-series/${moduleKey}`, { params }),
  seriesExport: (moduleKey, params) => http.get(`/excel/commodity-series-export/${moduleKey}`, {
    params,
    responseType: 'blob'
  })
}

// ─── Finance ──────────────────────────────────────────────────────────────
export const financeApi = {
  companies: (moduleKey = 'enterprise-finance', params = {}) => http.get(`/excel/finance-companies/${moduleKey}`, { params }),
  search: (moduleKey = 'enterprise-finance', keyword, marketType) => http.get(`/excel/rows/${moduleKey}`, { params: { keyword, marketType, pageSize: 20 } }),
  companyData: (moduleKey = 'enterprise-finance', code, marketType) => http.get(`/excel/finance-company/${moduleKey}`, { params: { code, marketType } }),
  exportCompany: (moduleKey, code, marketType) => http.get(`/excel/finance-company-export/${moduleKey}`, {
    params: { code, marketType },
    responseType: 'blob'
  })
}

// ─── Report ───────────────────────────────────────────────────────────────
export const reportApi = {
  topics: () => http.get('/report/topics'),
  createTopic: data => http.post('/report/topics', data),
  updateTopic: (id, data) => http.put(`/report/topics/${id}`, data),
  deleteTopic: id => http.delete(`/report/topics/${id}`),
  list: (params) => http.get('/report/list', { params }),
  get: (id) => http.get(`/report/${id}`),
  upload: (formData) => http.post('/report/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  create: (data) => http.post('/report', data),
  update: (id, data) => http.put(`/report/${id}`, data),
  delete: (id) => http.delete(`/report/${id}`),
  relatedSites: topicId => http.get('/report/related-sites', { params: { topicId } }),
  createRelatedSite: data => http.post('/report/related-sites', data),
  updateRelatedSite: (id, data) => http.put(`/report/related-sites/${id}`, data),
  deleteRelatedSite: id => http.delete(`/report/related-sites/${id}`)
}

// ─── News ─────────────────────────────────────────────────────────────────
export const newsApi = {
  list: params => http.get('/excel/news/news', { params })
}

// ─── Dict ─────────────────────────────────────────────────────────────────
export const dictApi = {
  items: dictKey => http.get(`/dict/${dictKey}`)
}

// ─── Excel / ChartData ────────────────────────────────────────────────────
export const excelApi = {
  chartData: moduleId => http.get(`/excel/chart-data/${moduleId}`),
  preview: (id, config = {}) => http.get(`/excel/datasets/${id}/preview`, {
    timeout: 10 * 60 * 1000,
    ...config
  }),
  rows: (moduleKey, params) => http.get(`/excel/rows/${moduleKey}`, { params, timeout: 60000 }),
  // 宏观宽表专用：指标为行、时间为列
  macroRows: (moduleKey, params) => http.get(`/excel/macro-rows/${moduleKey}`, { params }),
  macroSeries: (moduleKey, params) => http.get(`/excel/macro-series/${moduleKey}`, { params }),
  macroSeriesExport: (moduleKey, params) => http.get(`/excel/macro-series-export/${moduleKey}`, {
    params,
    responseType: 'blob'
  }),
  // 国际宏观专用：各国对比柱状图 + 单国折线图
  intlCompare: (moduleKey, params) => http.get(`/excel/intl-compare/${moduleKey}`, { params }),
  intlSeries: (moduleKey, params) => http.get(`/excel/intl-series/${moduleKey}`, { params }),
  datasets: params => http.get('/excel/datasets', { params }),
  upload: (formData, config = {}) => http.post('/excel/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 10 * 60 * 1000,
    ...config
  }),
  updateConfig: (id, data) => http.put(`/excel/datasets/${id}`, data),
  delete: (id, config = {}) => http.delete(`/excel/datasets/${id}`, {
    timeout: 10 * 60 * 1000,
    ...config
  })
}

// ─── Membership ───────────────────────────────────────────────────────────
export const membershipApi = {
  status: () => http.get('/membership/status')
}

// ─── Subscription ─────────────────────────────────────────────────────────
export const subscriptionApi = {
  plans: (params) => http.get('/subscription/plans', { params }),
  createOrder: data => http.post('/subscription/orders', data),
  getOrder: id => http.get(`/subscription/orders/${id}`),
  myOrders: () => http.get('/subscription/orders/mine'),
  allOrders: params => http.get('/subscription/orders', { params }),
  confirmPay: (id, data) => http.post(`/subscription/orders/${id}/pay`, data || {}),
  alipayPagePay: id => http.post(`/subscription/orders/${id}/alipay/page-pay`),
  payStatus: id => http.get(`/subscription/orders/${id}/pay-status`),
  cancelOrder: id => http.post(`/subscription/orders/${id}/cancel`),
  stats: () => http.get('/subscription/stats'),
  adminPlans: () => http.get('/subscription/plans/all'),
  createPlan: data => http.post('/subscription/plans', data),
  updatePlan: (id, data) => http.put(`/subscription/plans/${id}`, data),
  deletePlan: id => http.delete(`/subscription/plans/${id}`)
}

// ─── Access (细粒度模块权限) ───────────────────────────────────────────
export const accessApi = {
  check: moduleKey => http.get(`/access/check/${moduleKey}`),
  checkBatch: keys => http.get('/access/check-batch', { params: { keys } })
}

// ─── Knowledge (知识库) ──────────────────────────────────────────────
export const knowledgeApi = {
  list: params => http.get('/knowledge/list', { params }),
  adminList: params => http.get('/knowledge/admin/list', { params }),
  industries: () => http.get('/knowledge/industries'),
  adminIndustries: () => http.get('/knowledge/admin/industries'),
  createIndustry: data => http.post('/knowledge/industries', data),
  updateIndustry: (id, data) => http.put(`/knowledge/industries/${id}`, data),
  deleteIndustry: id => http.delete(`/knowledge/industries/${id}`),
  get: id => http.get(`/knowledge/${id}`),
  upload: formData => http.post('/knowledge/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => http.put(`/knowledge/${id}`, data),
  delete: id => http.delete(`/knowledge/${id}`),
  download: id => `${http.defaults.baseURL}/knowledge/download/${id}`,
  preview: id => `${http.defaults.baseURL}/knowledge/preview/${id}`
}

// ─── Settings (全局配置) ──────────────────────────────────────────
export const settingsApi = {
  get: () => http.get('/settings')
}

// ─── Analytics ────────────────────────────────────────────────────
export const analyticsApi = {
  track: data => http.post('/analytics/track', data).catch(() => {})
}

export default http
